import { useState, useCallback } from 'react';
import { generateAllSuggestions, type AISuggestion } from '@/lib/ai-preview';

interface AIAssistantPanelProps {
  productName: string;
  category: string;
  cost: number;
  onAcceptDescription: (description: string) => void;
  onAcceptSpecs: (specs: Record<string, string>) => void;
  onAcceptPrice: (price: number) => void;
}

export default function AIAssistantPanel({
  productName,
  category,
  cost,
  onAcceptDescription,
  onAcceptSpecs,
  onAcceptPrice,
}: AIAssistantPanelProps) {
  const [suggestions, setSuggestions] = useState<AISuggestion | null>(null);
  const [loading, setLoading] = useState(false);
  const [editDescription, setEditDescription] = useState('');
  const [editSpecs, setEditSpecs] = useState<Record<string, string>>({});

  const generateSuggestions = useCallback(async () => {
    if (!productName || !category) return;
    setLoading(true);
    const result = await generateAllSuggestions(productName, category, cost);
    setSuggestions(result);
    setEditDescription(result.description || '');
    setEditSpecs(result.specs || {});
    setLoading(false);
  }, [productName, category, cost]);

  const handleAcceptDescription = useCallback(() => {
    if (editDescription) {
      onAcceptDescription(editDescription);
    }
  }, [editDescription, onAcceptDescription]);

  const handleAcceptSpecs = useCallback(() => {
    if (Object.keys(editSpecs).length > 0) {
      onAcceptSpecs(editSpecs);
    }
  }, [editSpecs, onAcceptSpecs]);

  const handleAcceptPrice = useCallback(() => {
    if (suggestions?.suggestedPrice) {
      onAcceptPrice(suggestions.suggestedPrice);
    }
  }, [suggestions, onAcceptPrice]);

  return (
    <div className="rounded-xl border border-border bg-surface p-4">
      <div className="mb-4 flex items-center gap-2">
        <svg className="h-5 w-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
        <h3 className="font-semibold text-foreground">AI Assistant</h3>
      </div>

      <button
        type="button"
        onClick={generateSuggestions}
        disabled={loading || !productName || !category}
        className="btn-primary w-full"
      >
        {loading ? (
          <>
            <svg className="mr-2 h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Generating...
          </>
        ) : (
          'Generate Suggestions'
        )}
      </button>

      {suggestions && (
        <div className="mt-4 space-y-4">
          {suggestions.description && (
            <div>
              <div className="mb-2 flex items-center justify-between">
                <h4 className="text-sm font-medium text-foreground">Description</h4>
                <button type="button" onClick={handleAcceptDescription} className="text-xs text-primary-600 hover:underline">
                  Accept
                </button>
              </div>
              <textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                className="textarea-field text-sm"
                rows={4}
              />
            </div>
          )}

          {suggestions.specs && Object.keys(suggestions.specs).length > 0 && (
            <div>
              <div className="mb-2 flex items-center justify-between">
                <h4 className="text-sm font-medium text-foreground">Specifications</h4>
                <button type="button" onClick={handleAcceptSpecs} className="text-xs text-primary-600 hover:underline">
                  Accept All
                </button>
              </div>
              <div className="space-y-1">
                {Object.entries(editSpecs).map(([key, value]) => (
                  <div key={key} className="flex gap-2 text-sm">
                    <span className="w-32 flex-shrink-0 font-medium text-muted-foreground">{key}</span>
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => setEditSpecs({ ...editSpecs, [key]: e.target.value })}
                      className="input-field flex-1"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {suggestions.suggestedPrice && (
            <div className="rounded-lg border border-border p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Suggested Price</p>
                  <p className="text-lg font-bold text-foreground">SGD {suggestions.suggestedPrice.toFixed(2)}</p>
                  {suggestions.margin && (
                    <p className="text-xs text-muted-foreground">{suggestions.margin}% margin</p>
                  )}
                </div>
                <button type="button" onClick={handleAcceptPrice} className="btn-primary">
                  Apply Price
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
